import openCompose from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import { getStore } from '../store/composeStore';
import ComposeTarget from '../store/schema/ComposeTarget';
import { ComposeOperation } from '../store/schema/ComposeViewState';
import filterNdrSubmittedRecipients from '../utils/filterNdrSubmittedRecipients';
import { HTML_BODY_TYPE } from '../utils/getDefaultBodyType';
import { AppendOnSend } from 'owa-addins-core';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { getExtendedProperty } from 'owa-folders-common';
import getItem from 'owa-mail-store/lib/services/getItem';
import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import { getComposeItemResponseShape } from 'owa-mail-store/lib/utils/getItemResponseShape';
import isMeetingRequest from 'owa-meeting-message/lib/utils/isMeetingRequest';
import { isDeepLink } from 'owa-popout';
import BodyType from 'owa-service/lib/contract/BodyType';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import { isSxSDisplayed } from 'owa-sxs-store';
import { getStore as getTabStore, lazyActivateTab, TabType } from 'owa-tab-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { action } from 'satcheljs/lib/legacy';

export default action('loadDraftToCompose')(async function loadDraftToCompose(
    itemId: string,
    openTarget: ComposeTarget = ComposeTarget.SecondaryTab,
    isNdrResend?: boolean
) {
    const openInSxS = isSxSDisplayed() && openTarget !== ComposeTarget.Popout;
    const openInTab =
        !isDeepLink() && isTabViewEnabled() && openTarget === ComposeTarget.SecondaryTab;
    const store = getStore();

    if (!openInTab && !openInSxS) {
        if (openTarget == ComposeTarget.SecondaryTab) {
            // Can't open in tab, fallback to primary reading pane
            openTarget = ComposeTarget.PrimaryReadingPane;
        }

        await trySaveAndCloseCompose(null /*viewState*/);

        if (store.primaryComposeId) {
            return;
        }
    } else if (openInTab) {
        // Check if this draft is already opened in tab
        const tabStore = getTabStore();
        for (const tab of tabStore.tabs) {
            if (tab.type === TabType.Compose) {
                const viewState = store.viewStates.get(tab.data);

                if (viewState.itemId && viewState.itemId.Id == itemId) {
                    lazyActivateTab.importAndExecute(tab);
                    return;
                }
            }
        }
    }

    if (openInSxS) {
        openTarget = ComposeTarget.SxS;
    }

    // Pass null as body type to use original body type of the draft
    const shape = getComposeItemResponseShape(null /*bodyType*/);
    const response = await getItem([itemId], shape, 'MailCompose');
    if (checkIfResponseSuccess(response[0])) {
        const responseMessage = response[0] as ItemInfoResponseMessage;
        const message = responseMessage.Items[0] as Message;
        const bodyType: BodyType = message.Body.BodyType == HTML_BODY_TYPE ? 'HTML' : 'Text';
        let appendOnSend: AppendOnSend[] = getExtendedProperty(message, 'AppendOnSend');
        appendOnSend = !!appendOnSend ? appendOnSend : []; // if no value, initialize to empty list
        const isInlineCompose = false;
        const conversationId = !!message.ConversationId ? message.ConversationId.Id : undefined;
        const composeInitProps = {
            appendOnSend: appendOnSend,
            operation: ComposeOperation.EditDraft,
            bodyType: bodyType,
            itemId: message.ItemId,
            subject: message.Subject,
            quotedBody: message.Body.Value,
            to:
                isNdrResend && message.ToRecipients
                    ? filterNdrSubmittedRecipients(message.ToRecipients)
                    : message.ToRecipients,
            cc: message.CcRecipients,
            bcc: message.BccRecipients,
            attachments: message.Attachments,
            importance: message.Importance,
            isReadReceiptRequested: message.IsReadReceiptRequested,
            isDeliveryReceiptRequested: message.IsDeliveryReceiptRequested,
            preferAsyncSend: true,
            from: message.From != null ? message.From.Mailbox : null,
            pendingSocialActivityTags: message.PendingSocialActivityTagIds,
            IRMData: isEncryptionEnabled() ? message.RightsManagementLicenseData : null,
            meetingRequestItem: isMeetingRequest(message.ItemClass) != null ? { ...message } : null,
            sensitivity: message.Sensitivity,
            isInlineCompose: isInlineCompose,
            conversationId: conversationId,
        };

        await openCompose(composeInitProps, openTarget, null);
    }
});
